import * as React from 'react';
import { getRequestContext } from './server/request-context';

export function currentLanguage() {
  return __BROWSER__
    ? window.INITIAL_DATA.currentLanguage
    : getRequestContext().initialData!.clientData.currentLanguage!;
}

/**
 * Use this function to translate a common phrase. You pass in a function that resolves
 * the phrase you want, such as:
 *
 * const text = translate(t => t.commonPhrases.welcome);
 *
 * You can also pass in args for placeholders. That is, the translated text can contain
 * a placeholder that gets replaced by your call. If `commonPhrases.welcome` contains the
 * string `Hello {name}!` you can do:
 *
 * const text = translate(t => t.commonPhrases.welcome, {name: theCustomerName});
 *
 * where `theCustomerName` is a variable containing the current customers name.
 *
 * If you want to make the customers name bold for example, you should use the <Translation />
 * component instead of this function, as it allows you to pass in React elements as args.
 */
export function translate(f: TranslationFunction, args?: { [name: string]: string | number }) {
  const requestInDevOrOnServer = f;
  return translateInDevOrOnServer(requestInDevOrOnServer, args);
}

export type TranslationFunction = (t: Excite.LanguageStrings) => string;

export type TranslationProps = {
  text: ((t: Excite.LanguageStrings) => string) | string;
  args: { [placeholder: string]: string | number | React.ReactNode };
};

/**
 * Use this component when you need to include React elements in the translation placeholders/args.
 * A typical example is that you want to make the placeholder text bold, which you can do like this:
 *
 * <Translation text={t => t.myText, {searchphrase: <b>{searchPhrase}</b>}} />
 */
export function Translation(props: TranslationProps) {
  let text = props.text as unknown as string;
  if (typeof props.text === 'function') {
    text = translateInDevOrOnServer(props.text);
  }

  const children: Array<string | number | React.ReactNode> = [];
  let insidePlaceholder = false;
  let currentPlaceholder = '';
  let currentText = '';

  if (text) {
    for (const chr of text) {
      if (chr === '{') {
        insidePlaceholder = true;
        children.push(currentText);
        currentText = '';
      } else if (chr === '}') {
        insidePlaceholder = false;
        children.push(props.args[currentPlaceholder]);
        currentPlaceholder = '';
      } else if (insidePlaceholder) {
        currentPlaceholder += chr;
      } else {
        currentText += chr;
      }
    }
  }
  if (currentText) {
    children.push(currentText);
  }

  return <>{children}</>;
}

function translateInDevOrOnServer(request: TranslationFunction, args?: { [name: string]: string | number }) {
  const translations = __BROWSER__ ? window.EXCITE_TRANSLATIONS : getRequestContext().translations!;

  let phrase;
  if (!translations) {
    phrase = `(unknown language: ${currentLanguage})`;
  } else {
    const languageStrings = translations as unknown as Excite.LanguageStrings;
    // Changes "t => t.some.translationKey" to "some.translationKey".
    // We do this to match the missing translation text on the client and prevent ssr missmatch errors.
    const parts = request?.toString().split(' => ');

    phrase = 'Missing translation for ' + (parts[1] || '').replace(parts[0] + '.', '');
    try {
      const maybePhrase = request(languageStrings);
      if (maybePhrase && typeof maybePhrase === 'string') {
        phrase = maybePhrase;
      }
    } catch (e) {}
  }
  if (args) {
    return replaceTranslationArgs(phrase, args);
  }
  return phrase;
}

export function replaceTranslationArgs(phrase: string, args: { [name: string]: any }) {
  phrase = phrase ?? '';
  for (const key in args) {
    phrase = phrase.replace(new RegExp('{' + key + '}', 'g'), args[key]);
  }
  return phrase;
}
